"use client"

import { useCallback, useState } from "react"

import { XIcon } from "lucide-react"
import { useTranslations } from "next-intl"

import ChatPanel from "../chat-panel"
import {
  ChatSheet,
  ChatSheetClose,
  ChatSheetContent,
  ChatSheetDescription,
  ChatSheetTitle,
} from "@/components/ui/chat-sheet"

type Props = {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Conversation to open right away, e.g. right after creating one. */
  initialConversationId?: string | null
}

const ChatModal = ({ open, onOpenChange, initialConversationId }: Props) => {
  const t = useTranslations("AccountMessages")
  const [headerSlot, setHeaderSlot] = useState<HTMLDivElement | null>(null)

  const handleOpenChange = useCallback(
    (next: boolean) => {
      // Drop the portaled thread header together with the panel.
      if (!next) setHeaderSlot(null)
      onOpenChange(next)
    },
    [onOpenChange]
  )

  return (
    <ChatSheet open={open} onOpenChange={handleOpenChange}>
      <ChatSheetContent aria-describedby={undefined} showCloseButton={false}>
        {/* Header strip without a label: the close button sits exactly over
        the header chat icon (36px button, centered in the 64px header,
        16px from the right) so the same tap spot opens and closes. */}
        <div className="relative h-16 shrink-0 border-b border-ui-border-base">
          <div
            ref={setHeaderSlot}
            className="flex h-full min-w-0 items-center gap-2 pl-4 pr-14"
          />
          <ChatSheetTitle className="sr-only">{t("heading")}</ChatSheetTitle>
          <ChatSheetDescription className="sr-only">
            {t("description")}
          </ChatSheetDescription>
          <ChatSheetClose
            className="absolute right-4 top-1/2 flex size-9 -translate-y-1/2 items-center justify-center rounded-full transition-fg hover:bg-ui-bg-subtle"
            data-testid="close-chat"
          >
            <XIcon className="size-5" />
            <span className="sr-only">Close</span>
          </ChatSheetClose>
        </div>
        <div className="min-h-0 flex-1">
          {open && (
            <ChatPanel
              initialConversationId={initialConversationId}
              headerSlot={headerSlot}
            />
          )}
        </div>
      </ChatSheetContent>
    </ChatSheet>
  )
}

export default ChatModal
